/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import inflection from "inflection";

import { API, graphqlOperation } from "aws-amplify";
import {
  actionByActiveByEvent,
  apiKeyByActiveByName,
} from "../../../graphql/queries";
import { zapByActiveByEvent } from "../../../graphql/reloadyard/custom-queries";

import {
  Backdrop,
  Box,
  CircularProgress,
  DialogContentText,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import {
  Button,
  Confirm,
  Datagrid,
  DateField,
  FunctionField,
  Labeled,
  NumberField,
  TextField,
  useNotify,
} from "react-admin";

import { CreateIcon, DeleteIcon } from "../../../components/common/iconography";
import { SortedArrayField } from "../../../components/common/SortedArrayField";
import { logError } from "../../../utils/appUtils";
import {
  copyToClipboard,
  CopyToClipboardButton,
} from "../../../components/common/CopyToClipboardButton";
import { useThemeState } from "../../../ThemeStateProvider";
import { ZAPIER_CLIENT_ID } from "../../../utils/constants";

const useStyles = makeStyles(
  (theme) => ({
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: "#fff",
    },
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
    zapierContainer: {
      margin: theme.spacing(2, 0),
      padding: theme.spacing(2),
      border: "1px solid #FFFFFF64",
      borderRadius: 4,
    },
  }),
  { name: "RaDeleteWithConfirmButton" }
);

const renderOptions = (record) => {
  if (!record.options) return "None";

  try {
    const parsedOptions = JSON.parse(record.options);
    if (typeof parsedOptions === "object" && parsedOptions !== null) {
      return Object.entries(parsedOptions)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");
    }
    return String(parsedOptions);
  } catch (error) {
    return record.options; // Return raw string if parsing fails
  }
};

const getCreateConfirmContent = (apiKeyValue) => (
  <DialogContentText>
    Please save this API Key somewhere save and accessible. <br />
    You will only need it once to authenticate your Zap. <br />
    For security reasons, you will not be able to view it again through your
    account. <br />
    If you lose this API Key, you'll need to generate a new one. <br /> <br />
    {apiKeyValue}
  </DialogContentText>
);
const DeleteConfirmContent = (
  <DialogContentText>
    This API Key will be immediatelly disabled. <br />
    API requests made using this key will be rejected, which will cause any Zaps
    still relying on this key to break. <br />
    To reconnect, you will need to create a new API Key and re-authenticate
    through Zapier. <br />
    Are you sure you want to revoke this API Key?
  </DialogContentText>
);

const generateApiKey = async (record) => {
  if (!record?.id) return null;
  let resp = await API.post("zapierREST", "/generateApiKey", {
    body: {
      reloadYardId: record.id,
      reloadYardName: record.name,
      groups: record.groups,
      createdBy: window.appUser.fullName,
      apiName: "zapier",
    },
  });
  const apiKeyData = resp.apiKeyData;

  resp = await API.graphql(
    graphqlOperation(apiKeyByActiveByName, {
      reloadYardId: record.id,
      activeName: { eq: { active: "true", name: "zapier" } },
    })
  );
  const apiKey = {
    ...resp.data.apiKeyByActiveByName.items[0],
    value: apiKeyData.value,
  };
  return apiKey;
};

const deleteApiKey = async (apiKey) => {
  if (!apiKey?.id) return;
  await API.del("zapierREST", "/deleteApiKey", {
    body: {
      apiKey: apiKey,
      deletedBy: window.appUser.fullName,
    },
  });
};

const fetchZaps = async (
  record,
  setApiKey,
  setTriggers,
  setActions,
  notify
) => {
  try {
    const apikeyPromise = API.graphql(
      graphqlOperation(apiKeyByActiveByName, {
        reloadYardId: record.id,
        activeName: { eq: { active: "true", name: "zapier" } },
      })
    );
    const hookPromise = API.graphql(
      graphqlOperation(zapByActiveByEvent, {
        reloadYardId: record.id,
        activeEvent: { beginsWith: { active: "true" } },
      })
    );
    const actionPromise = API.graphql(
      graphqlOperation(actionByActiveByEvent, {
        reloadYardId: record.id,
        activeEvent: { beginsWith: { active: "true" } },
      })
    );

    const [apikeyResponse, hookRsponse, actionResponse] = await Promise.all([
      apikeyPromise,
      hookPromise,
      actionPromise,
    ]);
    setApiKey(apikeyResponse.data.apiKeyByActiveByName.items[0]);
    setTriggers(hookRsponse.data.zapByActiveByEvent);
    setActions(actionResponse.data.actionByActiveByEvent);
  } catch (err) {
    logError("reloadYard:ZapierPanel:fetchZaps", err, notify);
  }
};

const eventRenderer = (record) =>
  inflection.transform(record?.event, ["underscore", "humanize"]);

const ZapierPanel = (props) => {
  const { record, disabled } = props;
  const classes = useStyles();
  const notify = useNotify();
  const [apiKey, setApiKey] = useState(null);
  const [actions, setActions] = useState([]);
  const [triggers, setTriggers] = useState([]);

  useEffect(() => {
    fetchZaps(record, setApiKey, setTriggers, setActions, notify);
  }, [record]);

  if (disabled)
    return (
      <ZapShow
        {...props}
        apiKey={apiKey}
        triggers={triggers}
        actions={actions}
        classes={classes}
      />
    );
  return (
    <ZapEdit
      {...props}
      apiKey={apiKey}
      setApiKey={setApiKey}
      actions={actions}
      triggers={triggers}
      classes={classes}
      notify={notify}
    />
  );
};

const ZapEdit = (props) => {
  const { actions, apiKey, triggers } = props;
  const { palette } = useThemeState();
  const hasActions = apiKey && actions?.items?.length > 0;
  const hasTriggers = apiKey && triggers?.items?.length > 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <zapier-workflow
          sign-up-email={window.appUser.reloadYard?.email}
          sign-up-first-name={window.appUser.firstName}
          sign-up-last-name={window.appUser.lastName}
          client-id={ZAPIER_CLIENT_ID}
          theme={palette}
          intro-copy-display="show"
          manage-zaps-display={apiKey ? "show" : "hide"}
          app-search-bar-display={apiKey ? "show" : "hide"}
          template-limit={apiKey ? 6 : 0}
          guess-zap-display="hide"
          zap-create-from-scratch-display="hide"
        />
      </Grid>
      {hasTriggers && (
        <Grid item xs={12}>
          <Typography variant="h5">Active Triggers</Typography>
          <TriggersSection {...props} />
        </Grid>
      )}
      {hasActions && (
        <Grid item xs={12}>
          <Typography variant="h5">Active Actions</Typography>
          <ActionsSection {...props} />
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography variant="h5">API Key</Typography>
        <ApiKeySection {...props} />
      </Grid>
    </Grid>
  );
};

const ZapShow = (props) => {
  const { apiKey, actions, triggers } = props;
  const hasActions = apiKey && actions?.items?.length > 0;
  const hasTriggers = apiKey && triggers?.items?.length > 0;

  return (
    <Grid container spacing={2}>
      {hasTriggers && (
        <Grid item xs={12}>
          <Labeled label="TRIGGERS" fullWidth>
            <TriggersSection {...props} />
          </Labeled>
        </Grid>
      )}
      {hasActions && (
        <Grid item xs={12}>
          <Labeled label="ACTIONS" fullWidth>
            <ActionsSection {...props} />
          </Labeled>
        </Grid>
      )}
      <Grid item xs={12}>
        <Labeled label="API KEY" fullWidth>
          <ApiKeySection {...props} disabled={true} />
        </Labeled>
      </Grid>
    </Grid>
  );
};

const ActionsSection = (props) => {
  const { actions, classes } = props;

  return (
    <Grid container>
      <Grid item xs={12} className={classes.zapierContainer}>
        <SortedArrayField
          {...props}
          record={actions}
          source="items"
          sort={{ field: "event", order: "ASC" }}
          fullWidth
        >
          <Datagrid bulkActionButtons={false}>
            <FunctionField
              source="event"
              label="Event"
              render={eventRenderer}
            />
            <TextField source="description" label="Description" />
            <DateField source="createdAt" label="Created" />
            <NumberField source="counter" label="Usage Count" />
          </Datagrid>
        </SortedArrayField>
      </Grid>
    </Grid>
  );
};

const TriggersSection = (props) => {
  const { triggers, classes } = props;

  return (
    <Grid container>
      <Grid item xs={12} className={classes.zapierContainer}>
        <SortedArrayField
          {...props}
          record={triggers}
          source="items"
          sort={{ field: "event", order: "ASC" }}
        >
          <Datagrid>
            <FunctionField
              source="event"
              label="Event"
              render={eventRenderer}
            />
            <FunctionField
              source="options"
              label="Options"
              render={renderOptions}
            />
            <DateField source="createdAt" label="Created" />
          </Datagrid>
        </SortedArrayField>
      </Grid>
    </Grid>
  );
};

const ApiKeySection = (props) => {
  const { apiKey, setApiKey, disabled, classes, notify } = props;
  const [apiKeyValue, setApiKeyValue] = useState(null);
  const [open, setOpen] = useState(false);

  const handleDialogClose = () => setOpen(false);
  const handleCopy = () => {
    copyToClipboard(apiKeyValue, notify);
  };

  return (
    <Grid container>
      <Grid item xs={12} className={classes.zapierContainer}>
        <Grid container spacing={1}>
          <Grid item xs={4} md={3}>
            <Labeled label="Key ID" fullWidth>
              <TextField source="keyId" record={apiKey} />
            </Labeled>
          </Grid>
          <Grid item xs={4} md={2}>
            <Labeled label="Created" fullWidth>
              {
                // useRecordContext in DateField pulls the reloadYard context
                apiKey && <DateField source="createdAt" record={apiKey} />
              }
            </Labeled>
          </Grid>
          <Grid item xs={4} md={3}>
            <Labeled label="Created By" fullWidth>
              <TextField source="createdBy" record={apiKey} />
            </Labeled>
          </Grid>
          <Grid item xs={10} md={4}>
            {apiKeyValue && (
              <Labeled label="API Key" fullWidth>
                <Box display="flex" gap={1}>
                  <Typography>
                    {apiKeyValue.slice(0, 8) + "*".repeat(20)}
                  </Typography>
                  <CopyToClipboardButton text={apiKeyValue} />
                </Box>
              </Labeled>
            )}
          </Grid>
        </Grid>
        {!disabled && (
          <Grid item xs={12}>
            <Divider />
            <Box mt={1} display="flex" justifyContent="flex-end">
              {!apiKey && !disabled && (
                <CreateApiKeyButton
                  {...props}
                  setApiKey={setApiKey}
                  setApiKeyValue={setApiKeyValue}
                  setOpen={setOpen}
                  notify={notify}
                />
              )}
              {apiKey && !disabled && (
                <DeleteApiKeyButton
                  {...props}
                  apiKey={apiKey}
                  setApiKey={setApiKey}
                  setApiKeyValue={setApiKeyValue}
                  notify={notify}
                />
              )}
            </Box>
            {open && (
              <Confirm
                isOpen={open}
                title="API Key Confirmation"
                content={getCreateConfirmContent(apiKeyValue)}
                onConfirm={handleCopy}
                confirm="COPY"
                onClose={handleDialogClose}
                cancel="DONE"
              />
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const CreateApiKeyButton = ({
  record,
  setApiKey,
  setApiKeyValue,
  setOpen,
  notify,
  ...props
}) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);

  const handleCreate = async () => {
    setIsSaving(true);
    try {
      const newApiKey = await generateApiKey(record);
      setApiKey(newApiKey);
      setApiKeyValue(newApiKey?.value);
    } catch (error) {
      logError("reloadYard:zapierPanel:handleCreate", error, notify);
    } finally {
      setIsSaving(false);
      setOpen(true);
    }
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Button
        {...props}
        label="Create"
        size="large"
        onClick={handleCreate}
        disabled={isSaving}
      >
        <CreateIcon />
      </Button>
    </>
  );
};

const DeleteApiKeyButton = ({
  record,
  apiKey,
  setApiKey,
  setApiKeyValue,
  notify,
  className,
  ...props
}) => {
  const classes = useStyles();
  const [isDeleting, setIsDeleting] = useState(false);
  const [open, setOpen] = useState(false);

  const handleDialogOpen = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    setOpen(false);
    try {
      await deleteApiKey(apiKey);
      setApiKey(null);
      setApiKeyValue(null);
      notify("API Key deleted");
    } catch (error) {
      logError("reloadYard:zapierPanel:handleDelete", error, notify);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={isDeleting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Button
        {...props}
        label="Delete"
        size="large"
        onClick={handleDialogOpen}
        disabled={isDeleting}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className
        )}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={open}
        title="Delete Confirmation"
        content={DeleteConfirmContent}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};

export default ZapierPanel;
